import FunnelHeader from '@/components/v1/FunnelHeader'
import FunnelInput from '@/components/v1/FunnelInput'
import FunnelLayout from '@/components/v1/FunnelLayout'
import StickyFooter from '@/components/v1/StickyFooter'
import { FUNNEL_PROGRESS_SECTIONS } from '@/pages/v1/onboarding/funnelSteps'
import { setPasswordInSessionStorage } from '@app-studio-web/ui/helpers/sessionStorage'
import { validatePassword } from '@app-studio-web/ui/helpers/validations'
import { type IsUserSignedInLoaderData } from '@app-studio-web/ui/loaders/isUserSignedInLoader'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLoaderData, useNavigate } from 'react-router-dom'

/**
 * li-funnel-redesign v1 — the editorial password step. Mirrors the shared page's
 * validation and session-storage handoff to the email step.
 */
export default function PasswordPage() {
  const [hidePassword, setHidePassword] = useState(true)
  const [password, setPassword] = useState('')
  const [passwordValid, setPasswordValid] = useState(true)

  const { isUserSignedIn } = useLoaderData() as IsUserSignedInLoaderData
  const navigate = useNavigate()
  const { t } = useTranslation()

  if (isUserSignedIn) {
    return <Navigate to="/sign-up/preparing" />
  }

  const isPasswordValid = validatePassword(password)

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (isPasswordValid) {
      setPasswordInSessionStorage(password)
      navigate('/sign-up/email')
    } else {
      setPasswordValid(false)
    }
  }

  return (
    <FunnelLayout
      header={
        <FunnelHeader
          section="yourAccount"
          sectionProgress={2 / 3}
          sections={FUNNEL_PROGRESS_SECTIONS}
        />
      }
    >
      <div className="flex grow flex-col gap-5 pt-1">
        <div className="animate-fade-up space-y-1.5">
          <h1 className="hw-heading text-left!">
            {t('pages.signUp.password.heading')}
          </h1>

          <p className="hw-subheading text-left!">
            {t('pages.signUp.password.subheading')}
          </p>
        </div>

        <form className="flex grow flex-col" onSubmit={handleSubmit}>
          <div className="animate-fade-up space-y-2 [animation-delay:120ms]">
            <FunnelInput
              autoComplete="new-password"
              autoFocus
              dataTestId="textfield-password"
              invalid={!passwordValid}
              name="password"
              onChange={(event) => setPassword(event.target.value)}
              placeholder={t('shared.password')}
              trailing={
                <button
                  className="text-hw-muted flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-black/5"
                  onClick={() => setHidePassword(!hidePassword)}
                  type="button"
                >
                  {hidePassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              }
              type={hidePassword ? 'password' : 'text'}
            />

            <p
              className={
                passwordValid
                  ? 'text-hw-muted text-[13px]'
                  : 'text-hw-red text-[13px]'
              }
            >
              {t('pages.signUp.password.helperText')}
            </p>
          </div>

          <StickyFooter>
            <button
              className="hw-button-primary"
              data-testid="button-continue-credentials"
              disabled={password.length < 1}
              type="submit"
            >
              {t('shared.continue')}
            </button>
          </StickyFooter>
        </form>
      </div>
    </FunnelLayout>
  )
}
